import React, { useState, useEffect } from 'react';
import { inviteAPI } from '../lib/api';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  BarChart3, 
  Eye, 
  CheckCircle, 
  Clock, 
  XCircle,
  Mail,
  Loader2,
  TrendingUp
} from 'lucide-react';

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatTimeAgo(dateString) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const STATUS_CONFIG = {
  pending: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Clock, label: 'Pending' },
  accepted: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Accepted' },
  expired: { color: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: XCircle, label: 'Expired' },
  cancelled: { color: 'bg-red-100 text-red-600 border-red-200', icon: XCircle, label: 'Cancelled' },
};

export default function InviteStats({ projectId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (open) {
      loadStats();
    }
  }, [open, projectId]);

  const loadStats = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await inviteAPI.getInviteStats(projectId);
      setStats(res.data);
    } catch (error) {
      setErrorMsg(error.response?.data?.detail || 'Failed to load invite stats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          data-testid="invite-stats-btn"
          className="text-zinc-600 border-zinc-200"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Invite Stats
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Invitation Analytics
          </DialogTitle>
          <DialogDescription>
            Track views and acceptance of your team invitations
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : errorMsg ? (
          <p data-testid="invite-stats-error" className="text-center py-8 text-zinc-500">{errorMsg}</p>
        ) : stats && (
          <div className="space-y-6 mt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Mail className="w-4 h-4" />
                  <span className="text-xs font-medium">Total Sent</span>
                </div>
                <p data-testid="invite-stats-total-sent" className="text-2xl font-bold text-blue-700">{stats.total_sent}</p>
              </Card>
              
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">Accepted</span>
                </div>
                <p data-testid="invite-stats-accepted" className="text-2xl font-bold text-green-700">{stats.accepted}</p>
              </Card>
              
              <Card className="p-4 bg-orange-50 border-orange-200">
                <div className="flex items-center gap-2 text-orange-600 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs font-medium">Pending</span>
                </div>
                <p data-testid="invite-stats-pending" className="text-2xl font-bold text-orange-700">{stats.pending}</p>
              </Card>
              
              <Card className="p-4 bg-purple-50 border-purple-200">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Eye className="w-4 h-4" />
                  <span className="text-xs font-medium">Total Views</span>
                </div>
                <p data-testid="invite-stats-total-views" className="text-2xl font-bold text-purple-700">{stats.total_views}</p>
              </Card>
            </div>

            {/* Acceptance Rate */}
            {stats.total_sent > 0 && (
              <Card className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-zinc-600">Acceptance Rate</span>
                  <span className="text-sm font-bold text-zinc-900">
                    {Math.round((stats.accepted / stats.total_sent) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-zinc-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${(stats.accepted / stats.total_sent) * 100}%` }}
                  />
                </div>
              </Card>
            )}

            {/* Invites Table */}
            {stats.invites.length > 0 ? (
              <Card className="border-zinc-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Views</TableHead>
                      <TableHead>Last Viewed</TableHead>
                      <TableHead>Sent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.invites.map((invite) => {
                      const config = STATUS_CONFIG[invite.status] || STATUS_CONFIG.pending;
                      const StatusIcon = config.icon;
                      
                      return (
                        <TableRow key={invite.id} data-testid={`invite-stats-row-${invite.id}`}>
                          <TableCell className="font-medium">{invite.email}</TableCell>
                          <TableCell>
                            <Badge className={config.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {config.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span data-testid={`invite-stats-views-${invite.id}`} className="inline-flex items-center gap-1 text-purple-600">
                              <Eye className="w-3 h-3" />
                              {invite.view_count}
                            </span>
                          </TableCell>
                          <TableCell className="text-zinc-500 text-sm">
                            {invite.last_viewed_at ? formatTimeAgo(invite.last_viewed_at) : 'Never'}
                          </TableCell>
                          <TableCell className="text-zinc-500 text-sm">
                            {formatDate(invite.created_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <Mail className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
                <p>No invitations sent yet</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
